"use client";

import { useState } from "react";
import {
  type CipherMode,
  CipherWorkbench,
} from "@/components/tool-kit/cipher-workbench";
import {
  Checkbox,
  DataTable,
  ErrorNotice,
  Notice,
  type Option,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type BookAddress,
  type BookDecodeRow,
  type BookEncodeRow,
  type BookEntry,
  type BookPick,
  type BookUnit,
  bookDecode,
  bookEncode,
  MISSING_MARK,
} from "@/lib/tool-kit/ciphers-misc/book-cipher";

const ROW_LIMIT = 40;

/** English strings. A translated content file overrides these through `ui`. */
export const BOOK_CIPHER_STRINGS = {
  mode: "Mode",
  encode: "Encode",
  decode: "Decode",
  plainText: "Plain text",
  cipherText: "Positions in the book",
  decodedText: "Decoded text",
  inputPlaceholder: "Type or paste your message",
  cipherPlaceholder: "Positions such as 58 7 7 3 / 24 3",
  sample: "Meet at dawn",
  sampleWords: "The people dissolve",
  keyText: "Key text (the book)",
  keyHint:
    "Both people need exactly this text. Put a line containing only --- between pages. Lines with no words are not counted.",
  keyTextSample:
    "When in the Course of human events, it becomes necessary for one people\nto dissolve the political bands which have connected them with another,\nand to assume among the Powers of the earth, the separate and equal station\nto which the Laws of Nature and of Nature's God entitle them,\na decent respect to the opinions of mankind requires that they should declare\nthe causes which impel them to the separation.",
  unit: "What each position points at",
  unitFirstLetter: "First letter of a word",
  unitWord: "A whole word",
  unitLetter: "A single letter",
  address: "How positions are written",
  addressNumber: "One running number",
  addressLine: "Line : position",
  addressPage: "Page : line : position",
  pick: "When a word or letter occurs more than once",
  pickFirst: "Always use the first occurrence",
  pickRotate: "Take turns through the occurrences",
  pickHint:
    "Taking turns gives a repeated letter a different number each time, which hides the repeats a codebreaker looks for. Decoding is the same either way.",
  wordBreaks: "Write / between words",
  wordBreaksHint:
    "Without the slashes the decoder cannot tell where words end.",
  copy: "Copy result",
  copied: "Copied!",
  clear: "Clear",
  swap: "Swap input and output",
  working: "Working",
  workingEncodeIntro:
    "Each word or letter of the message is looked up in the key text. The position is what gets written.",
  workingDecodeIntro:
    "Each position is looked up in the key text and the word or letter found there is written down.",
  colPlain: "Message",
  colPosition: "Position",
  colReference: "Written",
  colFound: "Found",
  colWhere: "Where in the book",
  where: "Page {page}, line {line}, {unit} {n}: “{word}”",
  whereUnitWord: "word",
  whereUnitLetter: "letter",
  breakRow: "word break",
  notFoundRow: "not in the book",
  rowsShown:
    "one: Showing the first row.|other: Showing the first {count} rows.",
  missingWords: "Not in the key text, so written as ?: {items}",
  invalidRefs:
    "Not found in the book with these settings, so shown as ?: {items}",
  noKey: "Paste a key text to look positions up in.",
  noInput: "Type a message to see the working.",
} as const;

type Strings = typeof BOOK_CIPHER_STRINGS;

function whereText(
  entry: BookEntry,
  unit: BookUnit,
  strings: Strings,
  fmt: (value: number) => string
): string {
  return t(strings.where, {
    page: fmt(entry.page),
    line: fmt(entry.lineInPage),
    unit: unit === "letter" ? strings.whereUnitLetter : strings.whereUnitWord,
    n: fmt(entry.inLine),
    word: entry.word,
  });
}

function BookControls({
  strings,
  mode,
  keyText,
  onKeyText,
  unit,
  onUnit,
  address,
  onAddress,
  pick,
  onPick,
  wordBreaks,
  onWordBreaks,
}: {
  strings: Strings;
  mode: CipherMode;
  keyText: string;
  onKeyText: (value: string) => void;
  unit: BookUnit;
  onUnit: (value: BookUnit) => void;
  address: BookAddress;
  onAddress: (value: BookAddress) => void;
  pick: BookPick;
  onPick: (value: BookPick) => void;
  wordBreaks: boolean;
  onWordBreaks: (value: boolean) => void;
}) {
  const unitOptions: Option<BookUnit>[] = [
    { value: "first-letter", label: strings.unitFirstLetter },
    { value: "word", label: strings.unitWord },
    { value: "letter", label: strings.unitLetter },
  ];
  const addressOptions: Option<BookAddress>[] = [
    { value: "number", label: strings.addressNumber },
    { value: "line", label: strings.addressLine },
    { value: "page", label: strings.addressPage },
  ];
  const pickOptions: Option<BookPick>[] = [
    { value: "first", label: strings.pickFirst },
    { value: "rotate", label: strings.pickRotate },
  ];
  const encoding = mode === "encode";
  return (
    <div className="space-y-4">
      <TextAreaField
        hint={strings.keyHint}
        id="bk-key"
        label={strings.keyText}
        onChange={onKeyText}
        prose
        rows={6}
        value={keyText}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="bk-unit"
          label={strings.unit}
          onChange={onUnit}
          options={unitOptions}
          value={unit}
        />
        <SelectField
          id="bk-address"
          label={strings.address}
          onChange={onAddress}
          options={addressOptions}
          value={address}
        />
      </div>
      {encoding ? (
        <div className="space-y-3">
          <SelectField
            hint={strings.pickHint}
            id="bk-pick"
            label={strings.pick}
            onChange={onPick}
            options={pickOptions}
            value={pick}
          />
          {unit === "word" ? null : (
            <div className="space-y-1">
              <Checkbox
                checked={wordBreaks}
                label={strings.wordBreaks}
                onChange={onWordBreaks}
              />
              <p className={HINT}>{strings.wordBreaksHint}</p>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function BookCipherWidget() {
  const strings = useToolUi(BOOK_CIPHER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<CipherMode>("encode");
  const [input, setInput] = useState<string>(strings.sample);
  const [keyText, setKeyText] = useState<string>(strings.keyTextSample);
  const [unit, setUnit] = useState<BookUnit>("first-letter");
  const [address, setAddress] = useState<BookAddress>("number");
  const [pick, setPick] = useState<BookPick>("first");
  const [wordBreaks, setWordBreaks] = useState(true);

  const options = { unit, address, pick, wordBreaks };
  const encoded =
    mode === "encode" ? bookEncode(input, keyText, options) : null;
  const decoded =
    mode === "decode" ? bookDecode(input, keyText, options) : null;
  const output = encoded?.output ?? decoded?.output ?? "";

  function changeUnit(next: BookUnit) {
    const wasSample =
      mode === "encode" &&
      (input === strings.sample || input === strings.sampleWords);
    setUnit(next);
    if (wasSample) {
      setInput(next === "word" ? strings.sampleWords : strings.sample);
    }
  }

  function count(value: number) {
    return fmt(value);
  }

  const notices = (
    <div className="space-y-2">
      {keyText.trim().length === 0 ? <Notice>{strings.noKey}</Notice> : null}
      {encoded && encoded.missing.length > 0 ? (
        <ErrorNotice>
          {t(strings.missingWords, { items: encoded.missing.join(", ") })}
        </ErrorNotice>
      ) : null}
      {decoded && decoded.invalid.length > 0 ? (
        <ErrorNotice>
          {t(strings.invalidRefs, { items: decoded.invalid.join(", ") })}
        </ErrorNotice>
      ) : null}
    </div>
  );

  function encodeCells(row: BookEncodeRow): string[] {
    let where = "";
    if (row.entry) {
      where = whereText(row.entry, unit, strings, count);
    } else if (!row.isBreak) {
      where = strings.notFoundRow;
    }
    return [
      row.isBreak ? strings.breakRow : row.plain,
      row.ref ?? MISSING_MARK,
      where,
    ];
  }

  function decodeCells(row: BookDecodeRow): string[] {
    let found = row.entry?.text ?? MISSING_MARK;
    if (row.isBreak) {
      found = strings.breakRow;
    }
    return [
      row.ref,
      found,
      row.entry ? whereText(row.entry, unit, strings, count) : "",
    ];
  }

  const rows = encoded
    ? encoded.rows.map(encodeCells)
    : (decoded?.rows ?? []).map(decodeCells);

  const working =
    rows.length > 0 ? (
      <div className="space-y-3">
        <p className="text-muted-foreground text-sm leading-relaxed">
          {mode === "encode"
            ? strings.workingEncodeIntro
            : strings.workingDecodeIntro}
        </p>
        <DataTable
          head={
            mode === "encode"
              ? [strings.colPlain, strings.colReference, strings.colWhere]
              : [strings.colPosition, strings.colFound, strings.colWhere]
          }
          rows={rows.slice(0, ROW_LIMIT)}
        />
        {rows.length > ROW_LIMIT ? (
          <p className={HINT}>
            {tPlural(strings.rowsShown, locale, ROW_LIMIT, {
              count: fmt(ROW_LIMIT),
            })}
          </p>
        ) : null}
      </div>
    ) : (
      <Notice>{strings.noInput}</Notice>
    );

  return (
    <CipherWorkbench
      idPrefix="bk"
      input={input}
      keyControls={
        <BookControls
          address={address}
          keyText={keyText}
          mode={mode}
          onAddress={setAddress}
          onKeyText={setKeyText}
          onPick={setPick}
          onUnit={changeUnit}
          onWordBreaks={setWordBreaks}
          pick={pick}
          strings={strings}
          unit={unit}
          wordBreaks={wordBreaks}
        />
      }
      labels={{
        mode: strings.mode,
        encode: strings.encode,
        decode: strings.decode,
        input: mode === "encode" ? strings.plainText : strings.cipherText,
        inputPlaceholder:
          mode === "encode"
            ? strings.inputPlaceholder
            : strings.cipherPlaceholder,
        output: mode === "encode" ? strings.cipherText : strings.decodedText,
        copy: strings.copy,
        copied: strings.copied,
        clear: strings.clear,
        swap: strings.swap,
        working: strings.working,
      }}
      mode={mode}
      notices={notices}
      onInputChange={setInput}
      onModeChange={setMode}
      output={output}
      working={working}
    />
  );
}
